import React, { useState, useEffect, useRef } from 'react';
import { Stars } from 'lucide-react';

import { signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth } from './Auth';

import './Landing.css';

const Landing = () => {
  const [email, setEmail] = useState('');
  const [city, setCity] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const autocompleteRef = useRef(null);
  const cityInputRef = useRef(null);

  // Google Places Autocomplete setup
  useEffect(() => {
    // Load Google Maps JavaScript API script
    const loadGoogleMapsAPI = () => {
      const googleScript = document.createElement('script');
      googleScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAL9hRSkGJ_sy2eMy--qWmHafRnvkY_ZyE&libraries=places`;
      googleScript.async = true;
      googleScript.defer = true;
      googleScript.onload = initAutocomplete;
      document.head.appendChild(googleScript);
    };

    // Initialize Google Places Autocomplete
    const initAutocomplete = () => {
      if (cityInputRef.current && window.google) {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          cityInputRef.current,
          { types: ['(cities)'] }
        );
        
        // Update city state when a place is selected
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (place && place.name) {
            setCity(place.formatted_address);
          }
        });
      }
    };

    loadGoogleMapsAPI();

    return () => {
      // Clean up by removing the script
      const script = document.querySelector('script[src*="maps.googleapis.com/maps/api"]');
      if (script) {
        script.remove();
      }
    };
  }, []);

  // Handle scroll events for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Validate email format
  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  // Handle final submission with verification code
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!city) {
      alert('Please enter your city');
      return;
    }
    
    if (!email || !validateEmail(email)) {
      setIsEmailValid(false);
      return;
    }

    const response = await fetch('https://api-toymz7ykda-uc.a.run.app/email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, city }),
    });

    const data = await response.json();
    if (response.ok && data.code) {
      const userCredential = await signInWithEmailAndPassword(auth, email, data.code)
      await sendEmailVerification(userCredential.user);
    }
    
    setIsSubmitted(true);
  };

  return (
    <div className="app-container">
      {/* Navigation */}
      <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container navbar-container">
          <div className="logo">
            our<span className="logo-bold">vibe</span>
          </div>
          
          <div className="nav-links">
            <a href="/about" className="nav-link">About</a>
          </div>
        </div>
      </nav>

      {/* Hero Section with animated stars */}
      <main className="main-content">
        <div className="container hero-container">
          {/* Left Column (Text) */}
          <div className="hero-content">
            {/* Subtle glow behind headline */}
            <div className="headline-glow"></div>
            
            <h1 className="headline">
              <span className="headline-line">When souls align,</span>
              <span className="headline-gradient">time stands still.</span>
            </h1>
            
            <p className="subtitle">
              We use intelligence and intuition to help you connect with your person. Made for those seeking lasting depth beyond the surface.
            </p>
            
            {!isSubmitted ? (
              <form onSubmit={handleSubmit}>
              <div className="email-container">
                {/* Subtle glow effect */}
                <div className="email-glow"></div>
                
                {/* City input with Google Places Autocomplete */}
                <div className="email-input-container">
                  <input
                    ref={cityInputRef}
                    type="text"
                    placeholder="Your city"
                    className="email-input"
                  />
                </div>
                
                {/* Email input */}
                <div className="email-input-container margin-top">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setIsEmailValid(true);
                    }}
                    placeholder="Your email for early access"
                    className={`email-input ${!isEmailValid ? 'invalid' : ''}`}
                  />
                </div>
                
                {!isEmailValid && (
                  <p className="error-message">Please enter a valid email address</p>
                )}            
                {/* Submit button */}
                <div className="margin-top">
                <button
                  type='submit'
                  className="submit-button"
                >
                  Submit
                </button>
                </div>

                <div className="footertitle">
                  <b>Optional</b> - verify your .edu email to join the race to become the launch city for the Our Vibe app.
                </div>
              </div>
              </form>
            ) : (
              <div className="success-message">
                {/* Success animation */}
                <div className="success-gradient"></div>
                
                <div className="success-content">
                  <Stars className="success-icon" />
                  <div>
                    <p className="success-title">The stars have aligned.</p>
                    <p className="success-subtitle">We'll reach out when the time is right.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer-container">
          <div className="footer-logo-container">
            <div className="footer-logo">
              our<span className="footer-logo-bold">vibe</span>
            </div>
          </div>
          <div className="copyright">
            &copy; {new Date().getFullYear()} Our Vibe
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;